import { Capacitor } from '@capacitor/core';
import { DISTRIBUTION } from '@/config/distribution';
import { initializeFirebase, getToken, onMessage, Messaging } from '@/config/firebase';
import { Api } from '@/services/ApiService';
import { SchultenMedia } from '@/services/DataService';
import { navigateToNotification } from '@/utils/notificationRouter';

class WebPushService {
  private initialized = false;
  private messaging: Messaging | null = null;

  async initialize() {
    if (this.initialized) return;

    if (Capacitor.isNativePlatform()) {
      console.log('Web push not needed on native platforms');
      return;
    }

    // Web push is intentionally unavailable in development deployments. Vite's
    // DEV flag covers local development; VITE_MODE covers deployed dev builds.
    // The explicit switch can disable FCM in any other environment as well.
    const webPushDisabled = import.meta.env.DEV
      || import.meta.env.VITE_MODE === 'development'
      || import.meta.env.VITE_FIREBASE_ENABLED === 'false';
    if (webPushDisabled) {
      console.log('Web push disabled for this environment');
      return;
    }

    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('Service workers not supported');
      return;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return;
      }

      // Initialize Firebase
      this.messaging = initializeFirebase();
      if (!this.messaging) {
        console.log('Firebase messaging not available');
        return;
      }

      // Register Firebase messaging service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('Firebase SW registered:', registration);

      // Get FCM token
      const token = await getToken(this.messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        console.log('Web push token:', token);
        await this.registerDeviceToken(token);
      } else {
        console.log('No FCM token received');
      }

      // Handle foreground messages
      this.setupForegroundHandler();

      // Listen for notification click messages from service worker
      this.setupServiceWorkerMessageHandler();

      this.initialized = true;
      console.log('Web push initialized successfully');
    } catch (error) {
      console.error('Web push initialization error:', error);
    }
  }

  private setupForegroundHandler() {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload) => {
      console.log('Foreground message received:', payload);

      // Show browser notification for foreground messages
      if (Notification.permission === 'granted' && payload.notification) {
        const notification = new Notification(payload.notification.title || DISTRIBUTION.displayName, {
          body: payload.notification.body || '',
          icon: '/apple-touch-icon.png',
          badge: '/favicon-96x96.png',
          data: payload.data,
          tag: payload.data?.type || 'foreground',
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
          // Handle navigation based on data
          if (payload.data?.url) {
            // Backend sends full URL - extract path
            try {
              const url = new URL(payload.data.url);
              window.location.href = url.pathname;
            } catch {
              // If not a valid URL, try as path
              window.location.href = payload.data.url;
            }
          } else if (payload.data?.type) {
            navigateToNotification(payload.data.type, payload.data);
          }
        };
      }
    });
  }

  private setupServiceWorkerMessageHandler() {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        console.log('Notification click from SW:', event.data);
        const data = event.data.data || {};
        navigateToNotification(data.type, data);
      }
    });
  }

  private async registerDeviceToken(token: string) {
    try {
      const response = await Api.post(SchultenMedia.API.notification.saveToken, {
        device_token: token,
        platform: 'web',
      });

      if (response?.error) {
        console.error('Failed to register web push token:', response.message);
      } else {
        console.log('Web push token registered successfully');
      }
    } catch (error) {
      console.error('Error registering web push token:', error);
    }
  }

  cleanup() {
    this.initialized = false;
    this.messaging = null;
  }
}

export default new WebPushService();
